import piexif from 'piexifjs'

export function extractOrientationValue(a: ArrayBuffer): number {
  const view = new DataView(a)

  const length = view.byteLength
  let offset = 2
  let orientation = -1
  while (offset < length) {
    if (view.getUint16(offset + 2, false) <= 8) {
      orientation = -1
    }
    const marker = view.getUint16(offset, false)
    offset += 2
    // eslint-disable-next-line
    if (marker === 0xffe1) {
      if (view.getUint32((offset += 2), false) !== 0x45786966) {
        orientation = -1
        break
      }

      const little = view.getUint16((offset += 6), false) === 0x4949
      offset += view.getUint32(offset + 4, little)
      const tags = view.getUint16(offset, little)
      offset += 2
      for (let i = 0; i < tags; i++) {
        if (view.getUint16(offset + i * 12, little) === 0x0112) {
          orientation = view.getUint16(offset + i * 12 + 8, little)
        }
      }
      if (orientation > 0) {
        break
      }
      // eslint-disable-next-line
    } else if ((marker & 0xff00) !== 0xff00) {
      break
    } else {
      offset += view.getUint16(offset, false)
    }
  }
  return orientation
}

export function injectExif(
  base64: string,
  exifData: Record<string, any> = {},
  injectBackendExifOnly: boolean = false
) {
  try {
    if (!exifData || Object.keys(exifData).length === 0) {
      return base64
    }

    let filteredExif = exifData

    if (injectBackendExifOnly) {
      filteredExif = {
        '0th': {},
        Exif: {},
        GPS: {}
      }

      const { ImageIFD, ExifIFD, GPSIFD } = piexif

      // Add only required 0th tags
      if (exifData['0th']) {
        if (exifData['0th'][ImageIFD.Make]) {
          filteredExif['0th'][ImageIFD.Make] = exifData['0th'][ImageIFD.Make]
        }
        if (exifData['0th'][ImageIFD.Model]) {
          filteredExif['0th'][ImageIFD.Model] = exifData['0th'][ImageIFD.Model]
        }
        if (exifData['0th'][ImageIFD.Software]) {
          filteredExif['0th'][ImageIFD.Software] =
            exifData['0th'][ImageIFD.Software]
        }
      }

      // Add only DateTimeOriginal from Exif
      if (exifData.Exif && exifData.Exif[ExifIFD.DateTimeOriginal]) {
        filteredExif.Exif[ExifIFD.DateTimeOriginal] =
          exifData.Exif[ExifIFD.DateTimeOriginal]
      }

      // Add only needed GPS tags
      if (exifData.GPS) {
        const gps = exifData.GPS
        const neededGpsTags = [
          GPSIFD.GPSLatitudeRef,
          GPSIFD.GPSLatitude,
          GPSIFD.GPSLongitudeRef,
          GPSIFD.GPSLongitude
        ]

        for (const tag of neededGpsTags) {
          if (gps[tag]) {
            filteredExif.GPS[tag] = gps[tag]
          }
        }
      }
    }

    const exifBytes = piexif.dump(filteredExif)
    return piexif.insert(exifBytes, base64)
  } catch (_e) {
    return base64
  }
}

export function base64ToFile(dataURI: string, file: File) {
  let byteString

  if (dataURI.split(',')[0].includes('base64')) {
    byteString = atob(dataURI.split(',')[1])
  } else {
    byteString = decodeURI(dataURI.split(',')[1])
  }

  const mimestring = dataURI
    .split(',')[0]
    .split(':')[1]
    .split(';')[0]

  const content = []
  for (let i = 0; i < byteString.length; i++) {
    content[i] = byteString.charCodeAt(i)
  }

  return new File([new Uint8Array(content)], file.name, {
    type: mimestring
  })
}

export const blobUrlToFile = (
  blobUrl: string,
  title = 'image'
): Promise<File> =>
  new Promise(resolve => {
    fetch(blobUrl).then(async res => {
      const blob = await res.blob()
      const file = new File([blob], `${title}.png`, {
        type: 'image/png',
        lastModified: new Date().getTime()
      })
      resolve(file)
    })
  })

export function readImageMetadata(
  file: File
): Promise<{
  orientation: number
  make?: string
  exif?: Record<string, any>
}> {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = (e: any) => {
      try {
        const base64 = e.target?.result as string
        const exif = piexif.load(base64)

        const imageOrientationSupported = process.client
          ? document.createElement('img').style.imageOrientation !== undefined
          : false

        const orientation = imageOrientationSupported
          ? -1
          : exif?.['0th']?.[piexif.ImageIFD.Orientation] ?? -1

        const makeRaw = exif?.['0th']?.[piexif.ImageIFD.Make]
        let make: string | undefined

        if (typeof makeRaw === 'string') {
          make = makeRaw
        } else if (makeRaw instanceof Uint8Array || Array.isArray(makeRaw)) {
          try {
            make = new TextDecoder('utf-8').decode(new Uint8Array(makeRaw))
          } catch {
            make = undefined
          }
        }

        resolve({
          orientation,
          make,
          exif
        })
      } catch (_error) {
        resolve({ orientation: -1 })
      }
    }

    reader.readAsDataURL(file)
  })
}

export function resizeImage({
  canvas,
  initWidth,
  initHeight,
  maxResizeWidth,
  maxResizeHeight,
  minWidth,
  minHeight,
  orientation,
  image,
  eventWidth,
  eventHeight,
  file,
  make,
  backgroundColor = '#000000', // default fallback
  exif
}: {
  canvas: HTMLCanvasElement
  initWidth: number
  initHeight: number
  maxResizeWidth: number
  maxResizeHeight: number
  minWidth: number
  minHeight: number
  orientation: number
  image: any
  eventWidth: any
  eventHeight: any
  file: File
  make?: string
  exif?: any
  backgroundColor?: string
}) {
  function getImageAndThumb(canvas: HTMLCanvasElement) {
    let finalBase64 = canvas.toDataURL('image/jpeg')

    if (exif) {
      finalBase64 = injectExif(finalBase64, exif, true)
    }

    return {
      file: base64ToFile(finalBase64, file),
      thumb: canvas.toDataURL('image/jpeg'),
      width: canvas.width,
      height: canvas.height
    }
  }
  let width = initWidth
  let height = initHeight
  canvas.width = width
  canvas.height = height
  canvas.style.opacity = '0'

  let resized = false

  if (width > maxResizeWidth) {
    height *= maxResizeWidth / width
    width = maxResizeWidth
    resized = true
  }
  if (height > maxResizeHeight) {
    width *= maxResizeHeight / height
    height = maxResizeHeight
    resized = true
  }

  // repeat for minimum resizes
  if (width < minWidth) {
    height *= minWidth / width
    width = minWidth
    resized = true
  }
  if (height < minHeight) {
    width *= minHeight / height
    height = minHeight
    resized = true
  }

  if (resized) {
    canvas.width = width
    canvas.height = height
  }
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return getImageAndThumb(canvas)
  }

  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // transform context before drawing image
  if (orientation > 1) {
    switch (orientation) {
      case 2:
        // flip horizontal
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, width, height)
        break // supported
      case 3:
        // rotate 180 degrees left
        ctx.translate(width, height)
        ctx.rotate(-Math.PI)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, width, height)
        break // supported
      case 4:
        // vertical flip
        ctx.translate(0, height)
        ctx.scale(1, -1)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, width, height)
        break // supported
      case 5:
        // 90 rotate right + flip horizontal
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
        ctx.translate(width, 0)
        ctx.rotate((90 * Math.PI) / 180)

        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
        break // supported
      case 7:
        // horizontal flip + 90 rotate right
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
        ctx.translate(0, height)
        ctx.rotate((-90 * Math.PI) / 180)

        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
        break
      case 8:
        ctx.translate(0, height)
        ctx.rotate((-90 * Math.PI) / 180)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
        break
    }
    // @Hack for ipad images
    if (orientation === 6 && make === 'Apple') {
      ctx.translate(0, height)
      ctx.rotate((-90 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
    } else if (orientation === 6) {
      ctx.translate(width, 0)
      ctx.rotate((90 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
    }
  }

  // if it has orientation it will be drawn in the orientation case
  if (orientation <= 1) {
    if (resized) {
      ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height)
    } else {
      canvas.width = image.width
      canvas.height = image.height
      ctx.drawImage(image, 0, 0, image.width, image.height)
    }
  }

  return getImageAndThumb(canvas)
}
